import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { UID_NAME_TUPLES } from 'constants/dataSets';
import {
  useCustomerRecordQuery,
  useGetNaceCodeTreeQuery, useUidNameTuplesQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectUidNameTuples } from 'store/slices/dataSets/selectors';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import getCustomerDefaultValues from 'utils/formHelpers/getCustomerDefaultValues';
import { createCustomerSchema } from 'utils/validators';

import type { TCreateCustomerSchema } from 'utils/validators';
import type { ICreateCustomerForm } from '../interfaces/ICreateCustomerForm';

const useCreateCustomerForm = ({ recordId, isPageLoading }: ICreateCustomerForm) => {
  const fetchQuery = recordId ? {
    recordId,
  } : skipToken;
  const { data } = useCustomerRecordQuery(fetchQuery);
  const { data: profitCenterIds, isLoading: isProfitCenterIdsLoading } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const { data: naceCodeTreeData } = useGetNaceCodeTreeQuery();

  const { data: uidNameTuples } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const { data: locationsCountries } = useGetLocationsCountriesQuery();

  const {
    control,
    handleSubmit,
    formState,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
  } = useForm<TCreateCustomerSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(createCustomerSchema),
    defaultValues: getCustomerDefaultValues({
      values: data?.data, naceCodeTreeData, uidNameTuples, locationsCountries: locationsCountries?.data || [],
    }),
  });

  useEffect(() => {
    if (data?.data && !isPageLoading && !isProfitCenterIdsLoading) {
      reset(getCustomerDefaultValues(
        {
          values: data?.data,
          naceCodeTreeData,
          uidNameTuples,
          profitCenterIds,
          locationsCountries: locationsCountries?.data || [],
        },
      ));
    }
  }, [reset, data, isPageLoading, naceCodeTreeData, uidNameTuples, profitCenterIds,
    isProfitCenterIdsLoading, locationsCountries]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
  };
};

export default useCreateCustomerForm;
